import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Listens for the browser's `beforeinstallprompt` event and surfaces a
 * slim install banner when the app is installable (i.e. not already running
 * as a standalone PWA). The banner is dismissed permanently once the user
 * either installs or explicitly dismisses it.
 */
export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('stritchtunes:pwa-dismissed') === '1',
  );

  useEffect(() => {
    // Don't show if already running as installed PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      setPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('stritchtunes:pwa-dismissed', '1');
  };

  if (!prompt || dismissed) return null;

  return (
    <div
      className={cn(
        'fixed bottom-20 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2',
        'animate-in slide-in-from-bottom-4 fade-in duration-300',
      )}
    >
      <div className="flex items-center gap-3 rounded-2xl border border-violet-400/25 bg-[#1e1830]/95 px-4 py-3 shadow-2xl shadow-black/50 backdrop-blur-xl">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/20 text-violet-300">
          <Download className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white">Install StritchTunes</p>
          <p className="text-xs text-white/50">Add to home screen for the best experience</p>
        </div>
        <Button
          size="sm"
          onClick={() => void handleInstall()}
          className="shrink-0 bg-violet-500 text-white hover:bg-violet-400"
        >
          Install
        </Button>
        <button
          onClick={handleDismiss}
          className="shrink-0 rounded-lg p-1 text-white/40 transition hover:bg-white/8 hover:text-white"
          aria-label="Dismiss install prompt"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
